import type { RecordedAgenticChatSession } from 'features/embeddedSidebar/hooks/useAgenticChatSessionHistory'
import { getLastCompletedTurnId } from 'features/embeddedSidebar/sessions/documentChatConversationPersistence'
import { hasInProgressDocChatTurn } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatConversation'

export type SeenCompletedTurnIds = Readonly<Record<string, string | undefined>>

function latestCompletedTurnId(
  session: RecordedAgenticChatSession
): string | undefined {
  return (
    getLastCompletedTurnId(session.conversation.turns) ??
    session.lastCompletedTurnId
  )
}

/** Pending chats later receive a conversation id; last-seen is stored under the old key. */
function seenTurnLookup(
  session: RecordedAgenticChatSession,
  lastSeenByKey: SeenCompletedTurnIds
): { hadPrevious: boolean; seenId: string | undefined } {
  if (Object.hasOwn(lastSeenByKey, session.key)) {
    return { hadPrevious: true, seenId: lastSeenByKey[session.key] }
  }
  const pendingKey = session.conversation.pendingSessionKey
  if (pendingKey !== undefined && Object.hasOwn(lastSeenByKey, pendingKey)) {
    return { hadPrevious: true, seenId: lastSeenByKey[pendingKey] }
  }
  return { hadPrevious: false, seenId: undefined }
}

function hasSameSeenCompletedTurnIds(
  left: SeenCompletedTurnIds,
  right: SeenCompletedTurnIds
): boolean {
  const leftKeys = Object.keys(left)
  const rightKeys = Object.keys(right)
  if (leftKeys.length !== rightKeys.length) return false
  return leftKeys.every(
    (key) => Object.hasOwn(right, key) && left[key] === right[key]
  )
}

/** Longer than the slowest backend turn (time-series, 7 min) plus write grace. */
const STALE_RUNNING_TURN_MS = 10 * 60 * 1000

/**
 * Rolodex `active_turn` can stay `running` after the live conversation already
 * finished, or forever if the backend died mid-turn.
 */
export function isAgenticChatSessionThinking(
  session: RecordedAgenticChatSession,
  now: number = Date.now()
): boolean {
  if (hasInProgressDocChatTurn(session.conversation.turns)) return true
  return (
    session.isHydrated === false &&
    session.activeTurn?.status === 'running' &&
    now - session.updatedAt <= STALE_RUNNING_TURN_MS
  )
}

/** Seed last-seen on first sight; advance it only for the chat the user is viewing. */
export function nextSeenCompletedTurnIds({
  activeSessionKey,
  previous,
  sessions,
}: {
  activeSessionKey: string | undefined
  previous: SeenCompletedTurnIds
  sessions: readonly RecordedAgenticChatSession[]
}): SeenCompletedTurnIds {
  const next: Record<string, string | undefined> = {}
  sessions.forEach((session) => {
    const latest = latestCompletedTurnId(session)
    const { hadPrevious, seenId } = seenTurnLookup(session, previous)
    const shouldConsume = !hadPrevious || session.key === activeSessionKey
    if (shouldConsume) {
      next[session.key] = latest
      return
    }
    next[session.key] = seenId
  })
  return hasSameSeenCompletedTurnIds(previous, next) ? previous : next
}

export function unseenCompletedSessionKeys({
  activeSessionKey,
  lastSeenByKey,
  sessions,
}: {
  activeSessionKey: string | undefined
  lastSeenByKey: SeenCompletedTurnIds
  sessions: readonly RecordedAgenticChatSession[]
}): string[] {
  return sessions.flatMap((session) => {
    if (session.key === activeSessionKey) return []
    if (isAgenticChatSessionThinking(session)) return []
    const { hadPrevious, seenId } = seenTurnLookup(session, lastSeenByKey)
    if (!hadPrevious) return []
    const latest = latestCompletedTurnId(session)
    if (latest === undefined || latest === seenId) {
      return []
    }
    return [session.key]
  })
}
